import React from "react";
import { motion } from "framer-motion";
import { CheckCircle, Circle, ArrowRight } from "lucide-react";

const STAGES = [
  { key: "submitted", label: "Submitted" },
  { key: "in_review", label: "In Review" },
  { key: "underwriting", label: "Underwriting" },
  { key: "approved", label: "Approved" },
  { key: "capital_matching", label: "Capital Matching" },
  { key: "terms", label: "Terms" },
  { key: "funding", label: "Funding" },
  { key: "funded", label: "Funded" },
];

const STAGE_INDEX = {};
STAGES.forEach((s, i) => { STAGE_INDEX[s.key] = i; });
// Map legacy + intermediate stages to their closest display position
const ALIASES = { new_lead: 0, intake: 0, intake_complete: 0, needs_info: 1, docs_requested: 1, packaging: 3, matched: 4, submitted_to_lender: 4, terms_received: 5, conditional_approval: 6, clear_to_close: 6, active: 7 };

const NEXT_STEPS = {
  submitted: "Your application has been received. Our team is reviewing the details.",
  in_review: "An underwriter is reviewing your deal. You may be contacted for more info.",
  needs_info: "We need additional information or documents. Check the Documents tab.",
  underwriting: "Your deal is being underwritten. We're calculating risk metrics and lender fit.",
  approved: "Your deal has been approved! We're preparing it for capital matching.",
  capital_matching: "We're matching your deal with capital partners and lenders.",
  terms: "You have lender offers available. Review and select a lender to move forward.",
  funding: "Your deal is being funded! Our team is coordinating closing details.",
  funded: "Congratulations! Your deal has been funded.",
  active: "Your loan is active. Check distributions for payment details.",
  declined: "We were unable to approve your deal. Our team will follow up with alternative options.",
};

export default function DealProgressStepper({ stage, dealNumber }) {
  const rawIndex = STAGE_INDEX[stage] ?? ALIASES[stage] ?? 0;
  const currentIndex = stage === "declined" ? -1 : rawIndex;
  const isFunded = stage === "funded" || stage === "active";
  const isDeclined = stage === "declined";

  return (
    <div className="space-y-5" data-testid="deal-progress-stepper">
      {/* Visual stepper */}
      <div className="relative">
        {/* Progress bar background */}
        <div className="absolute top-4 left-4 right-4 h-0.5 bg-cream-warm" />
        {/* Progress bar fill */}
        <motion.div
          className="absolute top-4 left-4 h-0.5 bg-emerald-500"
          initial={{ width: 0 }}
          animate={{ width: `${Math.min((currentIndex / (STAGES.length - 1)) * 100, 100)}%` }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          style={{ maxWidth: "calc(100% - 32px)" }}
        />

        {/* Stage dots */}
        <div className="relative flex justify-between">
          {STAGES.map((s, i) => {
            const isComplete = !isDeclined && i < currentIndex;
            const isCurrent = !isDeclined && i === currentIndex;

            return (
              <div key={s.key} className="flex flex-col items-center" style={{ width: `${100 / STAGES.length}%` }}>
                <motion.div
                  initial={{ scale: 0.8 }}
                  animate={{ scale: isCurrent ? 1.15 : 1 }}
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-bold border-2 transition-all ${
                    isComplete ? "bg-emerald-500 border-emerald-500 text-white" :
                    isCurrent ? "bg-white border-emerald-500 text-emerald-600 shadow-md shadow-emerald-200" :
                    "bg-cream border-cream-warm text-[#ccc]"
                  }`}
                >
                  {isComplete ? <CheckCircle className="h-4 w-4" /> : (i + 1)}
                </motion.div>
                <span className={`text-[9px] mt-2 text-center leading-tight font-medium ${
                  isComplete ? "text-emerald-600" : isCurrent ? "text-emerald-700 font-bold" : "text-[#bbb]"
                }`}>{s.label}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Next Steps */}
      {!isFunded && !isDeclined && (
        <div className="rounded-xl border border-emerald-100 bg-emerald-50/50 p-4 flex items-start gap-3" data-testid="next-steps">
          <ArrowRight className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
          <div>
            <div className="text-[10px] uppercase tracking-wider text-emerald-600 font-bold mb-1">Next Steps</div>
            <p className="text-sm text-[#333]">{NEXT_STEPS[stage] || NEXT_STEPS.submitted}</p>
          </div>
        </div>
      )}

      {isFunded && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-3" data-testid="deal-funded-banner">
          <CheckCircle className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" />
          <div>
            <div className="text-sm font-bold text-emerald-700">Deal Funded</div>
            <p className="text-xs text-emerald-600 mt-0.5">Congratulations! Your deal {dealNumber ? `(${dealNumber})` : ""} has been funded.</p>
          </div>
        </div>
      )}

      {isDeclined && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 flex items-start gap-3" data-testid="deal-declined-banner">
          <Circle className="h-5 w-5 text-red-500 mt-0.5 shrink-0" />
          <div>
            <div className="text-sm font-bold text-red-600">Not Approved</div>
            <p className="text-xs text-red-500 mt-0.5">{NEXT_STEPS.declined}</p>
          </div>
        </div>
      )}
    </div>
  );
}
